import { useState } from "react";
import { Button, Descriptions, Drawer, Space, Spin, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import type { NcSourceData } from "@audit-plan/shared";
import { useNc } from "../../api/ncs";
import { useActions } from "../../api/actions";
import { useEvidenceList } from "../../api/evidence";
import { invalidateAudit, useAudit, useQuestions } from "../../api/audits";
import { usePeople } from "../../api/people";
import { useAuth } from "../../auth/AuthContext";
import { ActionStatusPill, DueLabel, Pill, SeverityPill, fmtD } from "../pills";
import { EvidencePanel } from "../evidence/EvidencePanel";
import { ActionDrawer } from "../action/ActionDrawer";
import { ActionFormModal } from "../action/ActionFormModal";
import { useFullDrawer } from "../audit/mobileModal";
import { useIsMobile } from "../useIsMobile";
import { NcFormModal } from "./NcFormModal";
import { ResizableTable } from "../ResizableTable";

/** Il resto della riga del registro 2026 di Pier (AUD-F8.2): elementi diretti di Descriptions, non un componente. */
function registryItems(d: NcSourceData, t: (k: string) => string) {
  const rows: [string, string | undefined][] = [
    [t("reg_source"), `${d.file === "registro_ital" ? t("reg_ital") : t("reg_noneu")} · ${t("reg_row")} ${d.row}`],
    [t("reg_supplier"), d.fornitoreRegistro + (d.abbinamento ? ` (${d.abbinamento})` : "")],
    [t("reg_detectedBy"), d.rilevataDa],
    [t("reg_detectedAt"), d.inData ? fmtD(d.inData) : undefined],
    [t("reg_theme"), d.tematica],
    [t("reg_type"), d.tipo],
    [t("reg_progress"), d.statoAvanzamento],
    [t("reg_outcome"), d.esito],
    [t("reg_closedRaw"), d.dataChiusuraEffettiva],
    [t("reg_effectiveness"), [d.verificaEfficacia ? fmtD(d.verificaEfficacia) : "", d.valutazioneEfficacia].filter(Boolean).join(" · ") || undefined],
    [t("country"), d.country],
    [t("category"), d.categoria],
  ];
  return rows.filter(([, v]) => v).map(([k, v]) => (
    <Descriptions.Item key={k} label={k}><span style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{v}</span></Descriptions.Item>
  ));
}

export function NcDrawer(props: { ncId: string | null; onClose: () => void }) {
  const { ncId, onClose } = props;
  const { t, i18n } = useTranslation();
  const { can } = useAuth();
  const people = usePeople();
  const qc = useQueryClient();
  const nc = useNc(ncId).data;
  const audit = useAudit(nc?.auditId).data;
  const qs = useQuestions(audit?.checklistVersionId).data ?? [];
  const actions = useActions({ ncId: ncId ?? undefined }, !!ncId).data ?? [];
  const ev = useEvidenceList({ targetType: "nc", targetId: ncId ?? undefined }, !!ncId).data ?? [];
  const [editing, setEditing] = useState(false);
  const [newAct, setNewAct] = useState(false);
  const [actId, setActId] = useState<string | null>(null);
  const mobile = useIsMobile();
  const fd = useFullDrawer(760);

  const refresh = () => {
    invalidateAudit(qc, nc?.auditId);
    void qc.invalidateQueries({ queryKey: ["nc", ncId] });
  };
  // NC del registro 2026 senza audit (AUD-F8.2): decidono admin e manager
  const ctx = nc && audit ? { auditorId: audit.auditorId, supplierId: nc.supplierId } : nc && !nc.auditId ? { supplierId: nc.supplierId } : undefined;
  const ed = !!nc && nc.status !== "closed" && !!ctx && can("nc.edit", ctx);
  const canAct = ed && !!audit && ["completed", "in_validation", "closed"].includes(audit.status) && can("action.create", ctx);
  const q = nc?.questionId ? qs.find((x) => x.id === nc.questionId) : undefined;
  const en = i18n.language === "en";

  const bs = mobile ? { size: "large" as const, style: { height: 48 } } : {};
  const acts = (
    <Space style={mobile ? { width: "100%" } : undefined}>
      {ed ? <Button {...bs} onClick={() => setEditing(true)}>{t("edit")}</Button> : null}
      {canAct ? (
        <Button {...bs} type="primary" icon={<PlusOutlined />} onClick={() => setNewAct(true)}>
          {t("newAction")}
        </Button>
      ) : null}
    </Space>
  );
  return (
    <Drawer
      open={!!ncId}
      onClose={onClose}
      {...fd}
      title={<span style={{ display: "block", whiteSpace: mobile ? "normal" : undefined, fontSize: mobile ? 15 : undefined, lineHeight: 1.3 }}>{nc ? `${nc.code} · ${nc.title}` : ""}</span>}
      destroyOnHidden
      extra={mobile ? null : acts}
      footer={mobile && (ed || canAct) ? acts : undefined}
    >
      {!nc ? (
        <Spin />
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Space wrap>
            <SeverityPill s={nc.severity} />
            <Pill tone={nc.status === "closed" ? "ok" : "bad"}>{nc.status === "closed" ? t("closed") : t("open")}</Pill>
            <Typography.Text type="secondary">
              {t("source")}: {nc.source === "registro-2026" ? t("reg_badge") : nc.auto ? t("auto") : t("manual")}
            </Typography.Text>
          </Space>
          <Descriptions column={1} size="small" bordered styles={mobile ? { label: { width: "34%" } } : undefined}>
            {/* la NC sta sullo stabilimento (AUD-F8.4) */}
            <Descriptions.Item label={t("f_factory")}>
              <Link to={`/factories/${nc.factoryId}`}>{nc.factoryName}</Link>
              {nc.factoryCode ? <span style={{ fontFamily: "var(--ap-mono)", opacity: 0.6 }}> {nc.factoryCode}</span> : null}
              {nc.factoryCountry ? <span style={{ opacity: 0.7 }}> · {nc.factoryCountry}</span> : null}
            </Descriptions.Item>
            <Descriptions.Item label={t("supplier")}>{nc.supplierName}</Descriptions.Item>
            <Descriptions.Item label="Audit">
              {nc.auditId ? <Link to={`/audits/${nc.auditId}`}>{nc.auditCode}</Link> : "—"}
            </Descriptions.Item>
            {nc.monitoringOff && nc.status === "open" ? <Descriptions.Item label={t("mon_off")}>{t(`mon_${nc.monitoringOff}`)}</Descriptions.Item> : null}
            {nc.sourceData ? registryItems(nc.sourceData, t) : null}
            {nc.questionId ? (
              <Descriptions.Item label={t("question")}>
                {nc.questionCode ?? q?.code} {q ? (en ? q.en || q.it : q.it) : ""}
              </Descriptions.Item>
            ) : null}
            <Descriptions.Item label={t("owner")}>{people.name(nc.ownerId)}</Descriptions.Item>
            <Descriptions.Item label={t("openDate")}>{fmtD(nc.createdAt)}</Descriptions.Item>
            {nc.closedAt ? <Descriptions.Item label={t("closed")}>{fmtD(nc.closedAt)}</Descriptions.Item> : null}
            <Descriptions.Item label={t("description")}>
              <span style={{ whiteSpace: "pre-wrap" }}>{nc.description}</span>
            </Descriptions.Item>
          </Descriptions>
          <div>
            <Typography.Title level={5}>{t("cap")}</Typography.Title>
            {mobile ? (
              <div style={{ display: "grid", gap: 8 }}>
                {actions.length === 0 ? <Typography.Text type="secondary">{t("none")}</Typography.Text> : null}
                {actions.map((a) => (
                  <div key={a.id} onClick={() => setActId(a.id)} style={{ border: "1px solid #E2DED3", borderRadius: 8, padding: 10, cursor: "pointer" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ fontFamily: "var(--ap-mono)" }}>{a.code}</span>
                      <ActionStatusPill a={a} />
                    </div>
                    <div style={{ fontWeight: 600, overflowWrap: "anywhere" }}>{a.title}</div>
                    <div style={{ fontSize: 12 }}><DueLabel a={a} /></div>
                  </div>
                ))}
              </div>
            ) : (
            <ResizableTable
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={actions}
              locale={{ emptyText: t("none") }}
              onRow={(a) => ({ onClick: () => setActId(a.id), style: { cursor: "pointer" } })}
              columns={[
                { title: t("code"), dataIndex: "code", render: (c: string) => <span style={{ fontFamily: "var(--ap-mono)" }}>{c}</span> },
                { title: t("title"), dataIndex: "title" },
                { title: t("dueDate"), render: (_: unknown, a) => <DueLabel a={a} /> },
                { title: t("status"), render: (_: unknown, a) => <ActionStatusPill a={a} /> },
              ]}
            />
            )}
          </div>
          <EvidencePanel
            evidence={ev}
            ctx={{ supplierId: nc.supplierId, auditId: nc.auditId, target: { type: "nc", id: nc.id } }}
            canUpload={can("evidence.upload", { supplierId: nc.supplierId })}
            onChanged={refresh}
          />
        </Space>
      )}
      {nc ? <NcFormModal open={editing} onClose={() => setEditing(false)} nc={nc} onSaved={refresh} /> : null}
      {nc ? <ActionFormModal open={newAct} onClose={() => setNewAct(false)} nc={nc} onSaved={refresh} /> : null}
      <ActionDrawer actionId={actId} onClose={() => setActId(null)} />
    </Drawer>
  );
}
